import Phaser from 'phaser';
import type { Room } from 'colyseus.js';
import {
  CLIENT_MESSAGES,
  ROOM_NAMES,
  SERVER_MESSAGES,
  type CreateRoomMessage,
  type GlobalChatMessage,
  type LobbyRoomEntry,
  type LobbyUserEntry,
  type ServerGlobalChatMessage,
  type ServerLobbyStateMessage,
  type ServerRoomCreatedMessage,
} from '@dalmuti/shared';
import { mountBackdrop } from '../ui/backdrop.js';
import { colyseusClient } from '../network/colyseus.js';
import {
  clearSession,
  getToken,
  getUser,
  isAdmin,
} from '../network/session.js';
import { resetOverlay } from '../ui/overlay.js';
import { transitionTo } from '../ui/transition.js';

interface ChatEntry {
  fromName: string;
  text: string;
  ts: number;
}

export class LobbyScene extends Phaser.Scene {
  private overlay: HTMLDivElement | null = null;
  private lobby: Room | null = null;
  private users: LobbyUserEntry[] = [];
  private rooms: LobbyRoomEntry[] = [];
  private chat: ChatEntry[] = [];
  private joining = false;
  private shuttingDown = false;

  constructor() {
    super('Lobby');
  }

  preload(): void {
    if (!this.textures.exists('title')) {
      this.load.image('title', '/title.png');
    }
  }

  create(): void {
    resetOverlay();

    // Phaser reuses this scene instance across visits, so instance fields
    // survive from the previous time we were in the lobby. Reset the
    // transient flags — otherwise `joining` left `true` from a successful
    // enterRoom (its success path never clears it) silently blocks every
    // future room entry after returning to the lobby.
    this.joining = false;
    this.shuttingDown = false;
    this.users = [];
    this.rooms = [];
    this.chat = [];

    // Atmospheric backdrop — heavily dimmed so the DOM lobby reads clearly.
    // (Stays matched to the viewport across rotations — see mountBackdrop.)
    mountBackdrop(this, {
      color: 0x1a0508,
      alpha: 0.85,
      image: 'title',
      imageAlpha: 0.06,
    });

    this.mountUI();
    void this.joinLobby();
    this.events.once(Phaser.Scenes.Events.SHUTDOWN, () => this.cleanup());
  }

  private async joinLobby(): Promise<void> {
    const token = getToken();
    if (!token) {
      this.goTo('Login');
      return;
    }
    try {
      const room = await colyseusClient.joinOrCreate(ROOM_NAMES.LOBBY, {
        token,
      });
      this.lobby = room;
      room.onMessage(
        SERVER_MESSAGES.LOBBY_STATE,
        (msg: ServerLobbyStateMessage) => {
          this.users = msg.users;
          this.rooms = msg.rooms;
          this.renderUsers();
          this.renderRooms();
        },
      );
      room.onMessage(
        SERVER_MESSAGES.GLOBAL_CHAT,
        (msg: ServerGlobalChatMessage) => {
          this.chat.push(msg);
          if (this.chat.length > 100) this.chat.shift();
          this.renderChat();
        },
      );
      room.onMessage(
        SERVER_MESSAGES.ROOM_CREATED,
        (msg: ServerRoomCreatedMessage) => {
          console.log('[LobbyScene] ROOM_CREATED', msg.roomId);
          // Defer one tick so the LOBBY_STATE message that lands right
          // before this (newly registered room) is fully processed first.
          // Joining inside the message callback synchronously has been
          // observed to occasionally hang the join handshake.
          setTimeout(() => {
            void this.enterRoom(msg.roomId);
          }, 50);
        },
      );
      room.onMessage(
        SERVER_MESSAGES.ERROR,
        (msg: { code: string; text: string }) => {
          this.setStatus(msg.text || msg.code);
        },
      );
      room.onLeave((code) => {
        this.lobby = null;
        // Ignore everything once we're tearing down (scene transition,
        // explicit leave to enter a room, etc).
        if (this.shuttingDown) return;
        if (code === 4001) {
          // Same-account session was kicked. Just notify — don't force a
          // logout (the user may have just hit refresh).
          this.setStatus('다른 곳에서 새로 로그인된 세션이 감지되었습니다');
        }
      });
      this.maybeAutoEnterFromHash();
    } catch (err) {
      this.setStatus(`로비 접속 실패: ${(err as Error).message}`);
      setTimeout(() => this.goTo('Login'), 2000);
    }
  }

  private maybeAutoEnterFromHash(): void {
    // Invite link format: #room/<roomId> or #spectate/<roomId>
    const hash = window.location.hash || '';
    const m = hash.match(/^#(room|spectate)\/([A-Za-z0-9_-]+)/);
    if (!m) return;
    const action = m[1];
    const roomId = m[2]!;
    // Clear so a back-out doesn't re-enter.
    history.replaceState(null, '', window.location.pathname);
    void this.enterRoom(roomId, action === 'spectate');
  }

  private async enterRoom(
    roomId: string,
    asSpectator = false,
  ): Promise<void> {
    console.log('[LobbyScene] enterRoom', { roomId, asSpectator, joining: this.joining });
    if (this.joining) {
      console.warn('[LobbyScene] enterRoom blocked by joining flag');
      return;
    }
    this.joining = true;
    // Safety: if anything hangs, release the flag after 8s so the user can
    // click again instead of being permanently stuck.
    const releaseTimer = window.setTimeout(() => {
      if (this.joining) {
        console.warn('[LobbyScene] enterRoom timed out, releasing joining flag');
        this.joining = false;
        this.setStatus('입장 응답이 늦어집니다 — 다시 시도해 주세요');
      }
    }, 8000);
    const token = getToken() ?? undefined;
    if (!roomId) {
      this.setStatus('방 ID가 없습니다');
      this.joining = false;
      clearTimeout(releaseTimer);
      return;
    }
    try {
      const room = await colyseusClient.joinById(roomId, {
        token,
        asSpectator,
      });
      clearTimeout(releaseTimer);
      console.log('[LobbyScene] joined', room.roomId, 'sessionId=', room.sessionId);
      // Leave the lobby socket so it doesn't compete with the game room.
      this.lobby?.leave().catch(() => {});
      this.lobby = null;
      // Spectators skip the waiting room and jump straight into the game view.
      this.goTo(asSpectator ? 'Game' : 'Room', { room });
    } catch (err) {
      clearTimeout(releaseTimer);
      console.error('[LobbyScene] enterRoom failed', err);
      this.setStatus(`방 입장 실패: ${(err as Error).message}`);
      this.joining = false;
    }
  }

  private goTo(key: string, data?: object): void {
    this.unmountUI();
    transitionTo(this, key, data);
  }

  private mountUI(): void {
    const root = document.getElementById('overlay');
    if (!root) return;
    const user = getUser();
    const wrap = document.createElement('div');
    wrap.style.cssText = `
      position: absolute;
      inset: 0;
      display: grid;
      grid-template-rows: 64px 1fr;
      pointer-events: auto;
      box-sizing: border-box;
    `;
    wrap.innerHTML = `
      <!-- Header bar -->
      <header style="display:flex; align-items:center; padding:0 24px; background: linear-gradient(180deg, rgba(245,200,105,0.08) 0%, rgba(0,0,0,0.0) 100%); border-bottom: 1px solid rgba(245,200,105,0.25); gap:18px;">
        <div style="display:flex; align-items:center; gap:14px;">
          <div style="position:relative; width:44px; height:44px; border-radius:12px;
               background: linear-gradient(150deg, #fff4c8 0%, #f5c869 38%, #c8922f 78%, #9a6c1e 100%);
               border:1px solid rgba(255,243,200,0.75);
               display:flex; align-items:center; justify-content:center;
               box-shadow: 0 6px 16px rgba(0,0,0,0.55), inset 0 1px 2px rgba(255,255,255,0.85), inset 0 -4px 7px rgba(120,80,20,0.45);">
            <span style="font-family: Georgia, 'Times New Roman', serif; font-weight:900; font-size:26px; color:#3a1d08; text-shadow:0 1px 0 rgba(255,255,255,0.45);">D</span>
            <span style="position:absolute; top:-8px; left:50%; transform:translateX(-50%); font-size:16px; filter: drop-shadow(0 1px 1px rgba(0,0,0,0.55));">👑</span>
            <span style="position:absolute; inset:3px 3px auto 3px; height:38%; border-radius:9px 9px 50% 50%; background:linear-gradient(180deg, rgba(255,255,255,0.55), rgba(255,255,255,0)); pointer-events:none;"></span>
          </div>
          <div>
            <div style="font-family: Georgia, 'Times New Roman', serif; font-size:25px; font-weight:800; letter-spacing:2.5px; line-height:1;
                 background: linear-gradient(180deg, #fff6d2 0%, #f5c869 48%, #c8922f 100%);
                 -webkit-background-clip:text; background-clip:text; -webkit-text-fill-color:transparent; color:#f5c869;
                 text-shadow:0 2px 5px rgba(0,0,0,0.45);">DALMUTI</div>
            <div style="display:flex; align-items:center; gap:7px; margin-top:4px;">
              <span style="width:18px; height:1px; background:linear-gradient(90deg, transparent, rgba(245,200,105,0.7));"></span>
              <span style="font-size:9px; color:rgba(245,222,170,0.75); letter-spacing:4px; font-weight:600;">ONLINE LOBBY</span>
              <span style="width:18px; height:1px; background:linear-gradient(90deg, rgba(245,200,105,0.7), transparent);"></span>
            </div>
          </div>
        </div>
        <div class="dm-catchphrase" style="flex:1; display:flex; justify-content:center; align-items:center; min-width:0;">
          <span style="font-family: Georgia, 'Times New Roman', serif; font-style:italic; font-size:15px; color:#c8922f; opacity:0.8; letter-spacing:0.5px; white-space:nowrap; overflow:hidden; text-overflow:ellipsis;">“Life isn't fair.”</span>
        </div>
        <div class="dm-header-meta" style="position:relative; display:flex; align-items:center; gap:14px; padding:6px 14px; background:rgba(255,255,255,0.05); border:1px solid rgba(245,200,105,0.2); border-radius:24px;">
          <div style="width:28px; height:28px; border-radius:50%; background: linear-gradient(135deg, #c89b2f 0%, #8a5e1f 100%); display:flex; align-items:center; justify-content:center; font-weight:700; color:#fff; font-size:12px; flex-shrink:0;">${escapeHtml((user?.username ?? '?').slice(0, 1).toUpperCase())}</div>
          <div class="dm-user-text" style="font-size:13px;">
            <div style="font-weight:700; color:#f4ecd6;">${escapeHtml(user?.username ?? 'Guest')}</div>
            <div style="font-size:10px; color:#f5c869;">RATING ${user?.rating ?? 0}</div>
          </div>
          <div class="dm-header-actions">
            <button id="dm-hall" class="lux-btn-ghost" style="padding:5px 12px; font-size:12px;">🏆 명예의 전당</button>
            ${isAdmin() ? '<button id="dm-admin" class="lux-btn-ghost" style="padding:5px 12px; font-size:12px;">관리자</button>' : ''}
            <button id="dm-logout" class="lux-btn-ghost" style="padding:5px 12px; font-size:12px;">로그아웃</button>
          </div>
          <button id="dm-hamburger" class="dm-hamburger lux-btn-ghost" aria-label="메뉴" style="padding:5px 11px; font-size:18px; line-height:1;">☰</button>
        </div>
      </header>

      <style>
        /* Lobby responsive: below 760px the room list is the main view, while
           online-users and chat collapse into bottom-sheet drawers opened by
           floating buttons. */
        #dm-lobby-fabs { display: none; }
        #dm-drawer-backdrop {
          display: none; position: fixed; inset: 0;
          background: rgba(8,3,5,0.6); z-index: 140;
        }
        .dm-drawer-handle { display: none; }
        .dm-hamburger { display: none; }
        /* Desktop default for the header actions (overridden into a dropdown
           below 760px). Defined in CSS — NOT inline — so the media query can
           hide it. */
        .dm-header-actions { display: flex; align-items: center; gap: 14px; }
        /* Always pin the user meta (avatar + actions/hamburger) to the right,
           even when the catchphrase spacer is hidden on mobile. */
        .dm-header-meta { margin-left: auto; }
        /* Narrow OR short (landscape phone) → compact drawers/hamburger. */
        @media (max-width: 760px), (max-height: 540px) {
          .dm-catchphrase { display: none !important; }
          .dm-user-text { display: none !important; }
          header { padding: 0 12px !important; gap: 8px !important; }
          /* Collapse the header buttons into a hamburger dropdown. */
          .dm-hamburger { display: flex !important; align-items: center; justify-content: center; }
          .dm-header-actions {
            position: absolute; top: calc(100% + 12px); right: 0;
            flex-direction: column; align-items: stretch; gap: 6px !important;
            background: linear-gradient(180deg, #2a1410 0%, #160a08 100%);
            border: 1px solid rgba(245,200,105,0.32); border-radius: 12px;
            padding: 8px; min-width: 168px; z-index: 200;
            box-shadow: 0 14px 34px rgba(0,0,0,0.6);
            display: none;
          }
          .dm-header-actions.dm-open { display: flex; }
          .dm-header-actions .lux-btn-ghost {
            width: 100%; text-align: left; padding: 11px 14px !important; font-size: 13px !important;
          }
          #dm-lobby-grid { grid-template-columns: 1fr !important; padding: 10px 10px 78px !important; }
          .dm-users-panel, .dm-chat-panel {
            position: fixed; left: 0; right: 0; bottom: var(--kb-inset, 0px);
            height: 74vh; max-height: calc(86vh - var(--kb-inset, 0px));
            margin: 0; z-index: 150;
            border-radius: 18px 18px 0 0;
            transform: translateY(106%);
            transition: transform 0.28s ease;
          }
          .dm-users-panel.dm-open, .dm-chat-panel.dm-open { transform: translateY(0); }
          .dm-drawer-handle { display: flex; }
          #dm-lobby-fabs { display: flex; }
          #dm-drawer-backdrop.dm-show { display: block; }
        }
      </style>

      <!-- Main grid -->
      <div id="dm-lobby-grid" style="display:grid; grid-template-columns: 280px 1fr 340px; gap:18px; padding:18px; min-height:0; box-sizing:border-box;">
        <!-- Left: online users -->
        <section class="lux-panel dm-users-panel">
          <div class="dm-drawer-handle" style="justify-content:space-between; align-items:center; margin-bottom:4px;">
            <span style="font-size:11px; color:rgba(244,236,214,0.5);">아래로 닫기</span>
            <button class="dm-drawer-close lux-btn-ghost" style="padding:3px 12px; font-size:13px;">✕</button>
          </div>
          <h3>접속자 <span id="dm-user-count" style="color:#f4ecd6; font-weight:500; font-size:12px;">0</span></h3>
          <div class="lux-divider"></div>
          <div id="dm-user-list" class="lux-scroll" style="overflow-y:auto; flex:1; display:flex; flex-direction:column; gap:4px; font-size:13px; padding-right:4px; min-height:0;"></div>
        </section>

        <!-- Middle: room list + create -->
        <section class="lux-panel dm-rooms-panel">
          <div style="display:flex; justify-content:space-between; align-items:center; gap:12px;">
            <h3 style="margin:0;">게임방 <span id="dm-room-count" style="color:#f4ecd6; font-weight:500; font-size:12px;">0</span></h3>
            <button id="dm-create-room" class="lux-btn-primary" style="padding:9px 18px;">＋ 방 만들기</button>
          </div>
          <div class="lux-divider"></div>
          <div id="dm-room-list" class="lux-scroll" style="overflow-y:auto; flex:1; display:flex; flex-direction:column; gap:8px; padding-right:4px; min-height:0;"></div>
          <div id="dm-status" style="font-size:12px; color:#ffb878; min-height:14px; text-align:center; padding-top:6px;"></div>
        </section>

        <!-- Right: global chat -->
        <section class="lux-panel dm-chat-panel">
          <div class="dm-drawer-handle" style="justify-content:space-between; align-items:center; margin-bottom:4px;">
            <span style="font-size:11px; color:rgba(244,236,214,0.5);">아래로 닫기</span>
            <button class="dm-drawer-close lux-btn-ghost" style="padding:3px 12px; font-size:13px;">✕</button>
          </div>
          <h3>전체 채팅</h3>
          <div class="lux-divider"></div>
          <div id="dm-chat-log" class="lux-scroll" style="overflow-y:auto; flex:1; display:flex; flex-direction:column; gap:4px; font-size:13px; padding:10px; background:rgba(0,0,0,0.25); border-radius:8px; border:1px solid rgba(245,200,105,0.12); min-height:0;"></div>
          <div style="display:flex; gap:6px; margin-top:8px;">
            <input id="dm-chat-input" class="lux-input" type="text" maxlength="240" enterkeyhint="send" autocomplete="off" placeholder="메시지를 입력하세요..." style="flex:1; padding:8px 10px; font-size:13px;">
            <button id="dm-chat-send" class="lux-btn-primary" style="padding:8px 14px;">전송</button>
          </div>
        </section>
      </div>

      <!-- Mobile drawer backdrop + floating buttons -->
      <div id="dm-drawer-backdrop"></div>
      <div id="dm-lobby-fabs" style="position:fixed; bottom:16px; left:50%; transform:translateX(-50%); gap:12px; z-index:135; pointer-events:auto;">
        <button id="dm-fab-users" class="lux-btn-primary" style="padding:12px 20px; font-size:14px; border-radius:24px; box-shadow:0 6px 16px rgba(0,0,0,0.5);">👥 접속자</button>
        <button id="dm-fab-chat" class="lux-btn-primary" style="padding:12px 20px; font-size:14px; border-radius:24px; box-shadow:0 6px 16px rgba(0,0,0,0.5);">💬 채팅</button>
      </div>
    `;
    root.appendChild(wrap);
    this.overlay = wrap;

    // Mobile drawers: floating buttons open the users / chat bottom sheets.
    const usersPanel = wrap.querySelector<HTMLElement>('.dm-users-panel');
    const chatPanel = wrap.querySelector<HTMLElement>('.dm-chat-panel');
    const backdrop = wrap.querySelector<HTMLElement>('#dm-drawer-backdrop');
    const closeDrawers = (): void => {
      usersPanel?.classList.remove('dm-open');
      chatPanel?.classList.remove('dm-open');
      backdrop?.classList.remove('dm-show');
    };
    const openDrawer = (panel: HTMLElement | null): void => {
      closeDrawers();
      panel?.classList.add('dm-open');
      backdrop?.classList.add('dm-show');
    };
    wrap
      .querySelector<HTMLButtonElement>('#dm-fab-users')
      ?.addEventListener('click', () => openDrawer(usersPanel));
    wrap
      .querySelector<HTMLButtonElement>('#dm-fab-chat')
      ?.addEventListener('click', () => openDrawer(chatPanel));
    backdrop?.addEventListener('click', closeDrawers);
    wrap
      .querySelectorAll<HTMLButtonElement>('.dm-drawer-close')
      .forEach((b) => b.addEventListener('click', closeDrawers));

    // Header hamburger dropdown (mobile): toggle the actions menu, close on
    // selecting an item or clicking outside.
    const hamburger = wrap.querySelector<HTMLButtonElement>('#dm-hamburger');
    const headerActions = wrap.querySelector<HTMLElement>('.dm-header-actions');
    const onDocClick = (ev: MouseEvent): void => {
      const t = ev.target as Node;
      if (!headerActions?.contains(t) && t !== hamburger) {
        headerActions?.classList.remove('dm-open');
        document.removeEventListener('click', onDocClick);
      }
    };
    hamburger?.addEventListener('click', (e) => {
      e.stopPropagation();
      const opening = !headerActions?.classList.contains('dm-open');
      headerActions?.classList.toggle('dm-open');
      if (opening) {
        setTimeout(() => document.addEventListener('click', onDocClick), 0);
      } else {
        document.removeEventListener('click', onDocClick);
      }
    });
    headerActions
      ?.querySelectorAll<HTMLButtonElement>('button')
      .forEach((b) =>
        b.addEventListener('click', () =>
          headerActions.classList.remove('dm-open'),
        ),
      );

    wrap
      .querySelector<HTMLButtonElement>('#dm-create-room')!
      .addEventListener('click', () => this.openCreateRoomModal());
    wrap
      .querySelector<HTMLButtonElement>('#dm-hall')!
      .addEventListener('click', () => {
        this.lobby?.leave().catch(() => {});
        this.lobby = null;
        this.goTo('Hall');
      });
    wrap
      .querySelector<HTMLButtonElement>('#dm-admin')
      ?.addEventListener('click', () => {
        this.lobby?.leave().catch(() => {});
        this.lobby = null;
        this.goTo('Admin');
      });
    wrap
      .querySelector<HTMLButtonElement>('#dm-logout')!
      .addEventListener('click', () => {
        clearSession();
        this.lobby?.leave().catch(() => {});
        this.goTo('Login');
      });

    const chatInput = wrap.querySelector<HTMLInputElement>('#dm-chat-input')!;
    const chatSend = wrap.querySelector<HTMLButtonElement>('#dm-chat-send')!;
    let lastSentText = '';
    let lastSentAt = 0;
    const sendChat = (): void => {
      const text = chatInput.value.trim();
      if (!text) return;
      // Block accidental dup sends (IME + Enter combos, double click, etc).
      const now = performance.now();
      if (text === lastSentText && now - lastSentAt < 500) {
        chatInput.value = '';
        return;
      }
      lastSentText = text;
      lastSentAt = now;
      chatInput.value = '';
      const payload: GlobalChatMessage = { text };
      this.lobby?.send(CLIENT_MESSAGES.GLOBAL_CHAT, payload);
    };
    chatSend.addEventListener('click', sendChat);
    chatInput.addEventListener('keydown', (e) => {
      // Korean IME: Enter during composition fires keydown with keyCode 229
      // (or isComposing=true). Ignoring it prevents the "half message + full
      // message" double-send.
      if (e.isComposing || e.keyCode === 229) return;
      if (e.key === 'Enter') sendChat();
    });
  }

  private renderUsers(): void {
    const list = this.overlay?.querySelector<HTMLDivElement>('#dm-user-list');
    const count = this.overlay?.querySelector<HTMLSpanElement>('#dm-user-count');
    if (!list || !count) return;
    count.textContent = `· ${this.users.length}명 온라인`;
    if (this.users.length === 0) {
      list.innerHTML = `<div style="padding:20px; text-align:center; color:rgba(244,236,214,0.4); font-size:12px;">아무도 없습니다</div>`;
      return;
    }
    list.innerHTML = this.users
      .map((u) => {
        const initial = (u.displayName || '?').slice(0, 1).toUpperCase();
        return `
        <div class="lux-list-row" style="display:flex; gap:8px; align-items:center;">
          <div style="width:24px; height:24px; border-radius:50%; background: linear-gradient(135deg, #c89b2f 0%, #8a5e1f 100%); display:flex; align-items:center; justify-content:center; font-weight:700; color:#fff; font-size:11px; flex-shrink:0;">${escapeHtml(initial)}</div>
          <span style="flex:1; color:#f4ecd6; font-size:13px; white-space:nowrap; overflow:hidden; text-overflow:ellipsis;">${escapeHtml(u.displayName)}</span>
          <span style="width:8px; height:8px; background:#82dd9c; border-radius:50%; flex-shrink:0; box-shadow:0 0 6px rgba(130,221,156,0.6);"></span>
        </div>`;
      })
      .join('');
  }

  private renderRooms(): void {
    const list = this.overlay?.querySelector<HTMLDivElement>('#dm-room-list');
    const count = this.overlay?.querySelector<HTMLSpanElement>('#dm-room-count');
    if (!list || !count) return;
    count.textContent = `· ${this.rooms.length}개`;
    if (this.rooms.length === 0) {
      list.innerHTML = `
        <div style="padding:40px 20px; text-align:center; color:rgba(244,236,214,0.45);">
          <div style="font-size:36px; opacity:0.4;">🎴</div>
          <div style="font-size:13px; margin-top:10px;">아직 열린 방이 없습니다</div>
          <div style="font-size:11px; margin-top:4px; color:rgba(244,236,214,0.3);">＋ 방 만들기로 첫 게임을 시작해 보세요</div>
        </div>`;
      return;
    }
    list.innerHTML = this.rooms
      .map((r) => {
        const waiting = r.status === 'waiting';
        const chipClass = waiting ? 'lux-chip-wait' : 'lux-chip-play';
        const chipLabel = waiting ? '대기중' : '진행중';
        const canJoin = waiting && r.currentPlayers < r.maxPlayers;
        const action = canJoin ? 'join' : 'spectate';
        const btnClass = canJoin ? 'lux-btn-primary' : 'lux-btn-ghost';
        const btnLabel = canJoin ? '입장' : '관전';
        return `
        <div class="lux-list-row" style="display:flex; gap:12px; align-items:center;">
          <div style="flex:1; min-width:0;">
            <div style="display:flex; align-items:center; gap:8px; margin-bottom:4px;">
              <span style="font-weight:700; color:#f4ecd6; font-size:14px; white-space:nowrap; overflow:hidden; text-overflow:ellipsis;">${escapeHtml(r.name)}</span>
              <span class="lux-chip ${chipClass}">${chipLabel}</span>
            </div>
            <div style="font-size:11px; color:rgba(244,236,214,0.55); display:flex; gap:10px; align-items:center;">
              <span>👑 ${escapeHtml(r.hostName)}</span>
              <span>· 👥 ${r.currentPlayers}/${r.maxPlayers}</span>
              ${r.spectators > 0 ? `<span>· 👀 ${r.spectators}</span>` : ''}
            </div>
          </div>
          <button data-room="${r.roomId}" data-action="${action}" class="dm-room-join ${btnClass}" style="padding:8px 18px; font-size:13px; min-width:72px;">${btnLabel}</button>
        </div>`;
      })
      .join('');
    list
      .querySelectorAll<HTMLButtonElement>('.dm-room-join')
      .forEach((btn) => {
        btn.addEventListener('click', () => {
          const id = btn.dataset.room!;
          const action = btn.dataset.action;
          void this.enterRoom(id, action === 'spectate');
        });
      });
  }

  private renderChat(): void {
    const log = this.overlay?.querySelector<HTMLDivElement>('#dm-chat-log');
    if (!log) return;
    if (this.chat.length === 0) {
      log.innerHTML = `<div style="text-align:center; color:rgba(244,236,214,0.35); font-size:12px; margin-top:auto; margin-bottom:auto;">아직 채팅이 없습니다</div>`;
      return;
    }
    log.innerHTML = this.chat
      .map(
        (c) =>
          `<div style="color:#f4ecd6;"><span style="color:#f5c869; font-weight:700;">${escapeHtml(c.fromName)}</span> <span style="color:rgba(244,236,214,0.55);">:</span> <span>${escapeHtml(c.text)}</span></div>`,
      )
      .join('');
    log.scrollTop = log.scrollHeight;
  }

  private openCreateRoomModal(): void {
    const root = document.getElementById('overlay');
    if (!root) return;
    const backdrop = document.createElement('div');
    backdrop.style.cssText = `
      position: fixed; inset: 0;
      background: rgba(10, 4, 6, 0.75);
      backdrop-filter: blur(4px);
      display: flex; align-items: center; justify-content: center;
      pointer-events: auto; z-index: 200;
    `;
    const modal = document.createElement('div');
    modal.className = 'lux-panel';
    modal.style.cssText = `width: 460px; pointer-events: auto; padding: 22px;`;
    const user = getUser();
    const defaultName = user ? `${user.username}의 방` : '새 방';
    modal.innerHTML = `
      <div style="display:flex; justify-content:space-between; align-items:center;">
        <h3 style="margin:0;">방 만들기</h3>
        <button id="dm-modal-close" class="lux-btn-ghost" style="padding:4px 10px; font-size:14px;">✕</button>
      </div>
      <div class="lux-divider"></div>
      <div style="display:flex; flex-direction:column; gap:16px; margin-top:8px;">
        <div>
          <div style="font-size:12px; color:#f5c869; font-weight:700; margin-bottom:6px; letter-spacing:0.5px;">방 이름</div>
          <input id="dm-room-name" class="lux-input" type="text" maxlength="32" value="${escapeHtml(defaultName)}" style="width:100%; padding:10px 12px; font-size:14px; box-sizing:border-box;">
        </div>
        <div>
          <div style="font-size:12px; color:#f5c869; font-weight:700; margin-bottom:6px; letter-spacing:0.5px;">최대 인원</div>
          <div id="dm-max-row" style="display:flex; gap:4px;">
            ${[2, 3, 4, 5, 6, 7, 8].map((n) => `<button data-max="${n}" class="dm-max-btn lux-btn-ghost" style="flex:1; padding:10px 0;">${n}</button>`).join('')}
          </div>
        </div>
        <div style="font-size:11px; color:rgba(244,236,214,0.55); padding:8px 10px; background:rgba(245,200,105,0.06); border-left:2px solid rgba(245,200,105,0.4); border-radius:4px;">
          💡 상세 옵션(상납/혁명/봇 등)은 방 입장 후 방장이 설정합니다.
        </div>
      </div>
      <button id="dm-create-confirm" class="lux-btn-primary" style="margin-top:20px; width:100%; padding:12px; font-size:14px;">방 만들기</button>
    `;
    backdrop.appendChild(modal);
    root.appendChild(backdrop);

    let selectedMax = 4;
    const refreshMax = (): void => {
      modal.querySelectorAll<HTMLButtonElement>('.dm-max-btn').forEach((b) => {
        const v = Number(b.dataset.max);
        const active = v === selectedMax;
        b.classList.toggle('lux-btn-primary', active);
        b.classList.toggle('lux-btn-ghost', !active);
      });
    };
    modal.querySelectorAll<HTMLButtonElement>('.dm-max-btn').forEach((b) => {
      b.addEventListener('click', () => {
        selectedMax = Number(b.dataset.max);
        refreshMax();
      });
    });
    refreshMax();

    const close = (): void => backdrop.remove();
    modal
      .querySelector<HTMLButtonElement>('#dm-modal-close')!
      .addEventListener('click', close);
    backdrop.addEventListener('click', (e) => {
      if (e.target === backdrop) close();
    });
    modal
      .querySelector<HTMLButtonElement>('#dm-create-confirm')!
      .addEventListener('click', () => {
        const nameInput = modal.querySelector<HTMLInputElement>('#dm-room-name')!;
        const payload: CreateRoomMessage = {
          name: nameInput.value.trim() || defaultName,
          maxPlayers: selectedMax,
        };
        this.lobby?.send(CLIENT_MESSAGES.CREATE_ROOM, payload);
        close();
      });
  }

  private setStatus(text: string): void {
    const el = this.overlay?.querySelector<HTMLDivElement>('#dm-status');
    if (el) el.textContent = text;
  }

  private cleanup(): void {
    this.shuttingDown = true;
    this.unmountUI();
    if (this.lobby) {
      this.lobby.leave().catch(() => {});
      this.lobby = null;
    }
  }

  private unmountUI(): void {
    this.overlay?.remove();
    this.overlay = null;
  }
}

function escapeHtml(s: string): string {
  return s
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;');
}
